// auth/interceptors/jwt.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/api/auth.service';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
    const auth = inject(AuthService);
    const token = auth.getToken();

    // Skip only public auth endpoints (login, refresh, register, forgot-password)
    const publicAuthEndpoints = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/forgot-password'];
    if (publicAuthEndpoints.some((e) => req.url.includes(e))) return next(req);

    // If no token, proceed without auth
    if (!token) return next(req);

    // Proactive refresh if token is expired or about to expire (within 1 min)
    if (auth.isTokenExpired(token, 60000)) {
        return auth.refreshToken().pipe(
            switchMap(() => {
                const newToken = auth.getToken();
                if (!newToken) return next(req);
                const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
                return next(authReq);
            }),
            catchError(() => {
                auth.logout();
                return throwError(() => new Error('Session expired'));
            })
        );
    }

    // Token is still valid — attach and proceed
    const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

    return next(authReq).pipe(
        catchError((err: HttpErrorResponse) => {
            if (err.status === 401) {
                return auth.refreshToken().pipe(
                    switchMap(() => {
                        const newToken = auth.getToken();
                        const retried = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
                        return next(retried);
                    }),
                    catchError((refreshErr) => {
                        auth.logout();
                        return throwError(() => refreshErr);
                    })
                );
            }
            return throwError(() => err);
        })
    );
};
